import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AUTH_CONFIG } from '../config';

const AdminLayout = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const location = useLocation();
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking authentication status for AdminLayout...');
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Determine which tab is active
  const currentPath = location.pathname;
  
  const isActive = (path) => {
    if (path === '/admin' && currentPath === '/admin') return true;
    if (path === '/admin/policies' && currentPath.startsWith('/admin/policies')) return true;
    if (path === '/admin/opinions' && currentPath.startsWith('/admin/opinions')) return true;
    return false;
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white shadow-md rounded-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Authentication Required</h2>
            <p className="mt-2 text-gray-600">Please log in to access the admin panel.</p>
          </div>
          <div className="text-center">
            <Link
              to="/admin"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
              Go to Admin Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Cult of the Red Hat Admin</h1>
          <div className="flex gap-2">
            <button 
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200"
            >
              Home
            </button>
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
            >
              View Site
            </Link>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <Link
                to="/admin"
                className={`${
                  isActive('/admin')
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
              >
                Add New Claim
              </Link>
              <Link
                to="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/admin';
                  localStorage.setItem('adminActiveTab', 'manage');
                }}
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Manage Claims
              </Link>
              <Link
                to="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/admin';
                  localStorage.setItem('adminActiveTab', 'tags');
                }}
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Manage Tags
              </Link>
              <Link
                to="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/admin';
                  localStorage.setItem('adminActiveTab', 'framework');
                }}
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Democratic Framework
              </Link>
              <Link
                to="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/admin';
                  localStorage.setItem('adminActiveTab', 'articles');
                }}
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Articles
              </Link>
              <Link
                to="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/admin';
                  localStorage.setItem('adminActiveTab', 'politifact');
                }}
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                PolitiFact Scraper
              </Link>
              <Link
                to="/admin/policies"
                className={`${
                  isActive('/admin/policies')
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
              >
                Policies
              </Link>
              <Link
                to="/admin/opinions"
                className={`${
                  isActive('/admin/opinions')
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
              >
                Opinions
              </Link>
            </nav>
          </div>
        </div>
        
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;